import { useQuery } from '@tanstack/react-query';
import type {
  GetPatientFormularyForDrugParams,
  PatientFormularyResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { fetchPatientFormulary } from '../api/getPatientFormulary.js';
import { type PatientFormularyQueryKeyParams, rxKeys } from './keys.js';

export interface UsePatientFormularyQueryInput {
  userId: string;
  params: PatientFormularyQueryKeyParams;
  enabled?: boolean;
}

/**
 * Live formulary lookup for a single drug under the patient's plan.
 *
 * Calls ``GET /dosespot/patients/{user_id}/formulary``. Surfaces
 * formulary status, drug-specific copays, payer-preferred alternatives,
 * therapeutic alternatives, and coverage restrictions for the selected
 * medication. Drives the formulary status badge on the Rx wizard's
 * Details step and the alternatives panel on the Review step.
 *
 * Disabled until at least ``dispensableDrugId`` (or ``ndc``) is set —
 * the rest of the params are optional and resolved server-side. We
 * include the same set in the query key so an idle medication change
 * triggers a re-fetch exactly when it should.
 *
 * Mirrors the debouncing strategy of ``usePreviewPriorAuthQuery``:
 * react-query dedupes successive renders with the same key so this is
 * safe to render high in the tree without extra throttling.
 */
export function usePatientFormularyQuery({
  userId,
  params,
  enabled = true,
}: UsePatientFormularyQueryInput) {
  const hasInputs = params.dispensableDrugId !== null || (params.ndc !== null && params.ndc !== '');
  return useQuery<PatientFormularyResponseT>({
    queryKey: rxKeys.patientFormulary(userId, params),
    queryFn: ({ signal }) => {
      const sdkParams: GetPatientFormularyForDrugParams = {
        dispensable_drug_id: params.dispensableDrugId ?? undefined,
        ndc: params.ndc ?? undefined,
        pharmacy_id: params.pharmacyId ?? undefined,
        patient_eligibility_id: params.patientEligibilityId ?? undefined,
      };
      return fetchPatientFormulary(userId, sdkParams, signal);
    },
    enabled: enabled && hasInputs,
    placeholderData: (previous) => previous,
    staleTime: 30_000,
    meta: {
      toast: {
        title: 'Could not load formulary',
        description:
          'Formulary status could not be fetched from DoseSpot. The prescription can still be created.',
      },
    },
  });
}
